/**
 * useHandDetection — React hook wrapping MediaPipe hand detection
 * 
 * Ported from: js/handDetection.js
 * 
 * Manages webcam stream, MediaPipe HandLandmarker, and the 
 * requestAnimationFrame detection loop.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { loadHandLandmarker, extractFeatures, drawLandmarks } from '../utils/mediapipe.js';

export function useHandDetection() {
    const [isModelLoaded, setIsModelLoaded] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isHandDetected, setIsHandDetected] = useState(false); // Triggers UI updates
    const [showVideo, setShowVideo] = useState(false); // Shared video visibility (defaults off)
    const [error, setError] = useState(null);

    // True once the user has started the camera at least once this session.
    // Tabs use this to auto-show the camera on mount instead of showing "Start Camera".
    const [wasStarted, setWasStarted] = useState(() => {
        try {
            return window.sessionStorage.getItem('camera_wasStarted') === 'true';
        } catch { return false; }
    });

    // Store landmarks in a ref to avoid re-rendering on every frame (~60fps).
    const currentLandmarksRef = useRef(null);

    const handLandmarkerRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const animFrameRef = useRef(null);
    const lastTimestampRef = useRef(-1);
    const debounceTimeoutRef = useRef(null);
    const lastDetectionStateRef = useRef(false);

    // Load the MediaPipe model
    const loadModel = useCallback(async () => {
        if (handLandmarkerRef.current) return;
        setIsLoading(true);
        try {
            handLandmarkerRef.current = await loadHandLandmarker();
            setIsModelLoaded(true);
        } catch (err) {
            console.error('Failed to load HandLandmarker:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Start webcam + detection loop
    const start = useCallback(async (videoElement, canvasElement) => {
        videoRef.current = videoElement;
        canvasRef.current = canvasElement;

        if (!handLandmarkerRef.current) {
            await loadModel();
        }

        try {
            let stream = streamRef.current;
            if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { width: 640, height: 480, facingMode: 'user' },
                });
                streamRef.current = stream;
            } else {
                if (animFrameRef.current) {
                    cancelAnimationFrame(animFrameRef.current);
                }
            }
            videoElement.srcObject = stream;
            await videoElement.play();

            setIsRunning(true);
            setWasStarted(true);
            try { window.sessionStorage.setItem('camera_wasStarted', 'true'); } catch { /* ignore */ }
            lastTimestampRef.current = -1;
            detectLoop();
            setError(null);
        } catch (err) {
            console.error('Failed to start webcam:', err);
            
            // Enhanced error handling with specific error types
            let errorMessage = 'Failed to start webcam';
            if (err.name === 'NotFoundError') {
                errorMessage = 'No camera device found. Please connect a webcam.';
            } else if (err.name === 'NotAllowedError') {
                errorMessage = 'Camera permission denied. Please allow camera access.';
            } else if (err.name === 'NotReadableError') {
                errorMessage = 'Camera is already in use by another application.';
            } else if (err.name === 'OverconstrainedError') {
                errorMessage = 'Camera constraints not supported. Try different settings.';
            } else if (err.name === 'SecurityError') {
                errorMessage = 'Camera access blocked for security reasons.';
            }
            
            // Create a custom error with the enhanced message
            const enhancedError = new Error(errorMessage);
            enhancedError.name = err.name;
            enhancedError.originalError = err;
            
            setError(enhancedError);
        }
    }, [loadModel]);

    // Detection loop
    const detectLoop = useCallback(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const handLandmarker = handLandmarkerRef.current;

        if (!video || !canvas || !handLandmarker) return;

        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        const tick = () => {
            if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return;

            // Ensure valid dimensions (fixes ROI width/height > 0 error)
            if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
                animFrameRef.current = requestAnimationFrame(tick);
                return;
            }

            const timestamp = performance.now();
            if (timestamp === lastTimestampRef.current) {
                animFrameRef.current = requestAnimationFrame(tick);
                return;
            }
            lastTimestampRef.current = timestamp;

            try {
                const result = handLandmarkerRef.current.detectForVideo(video, timestamp);
                const landmarks = result.landmarks;

                // Clear and draw
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                drawLandmarks(ctx, landmarks, canvas.width, canvas.height);

                const hasHands = landmarks && landmarks.length > 0;
                currentLandmarksRef.current = hasHands ? landmarks : null;

                // Debounce UI updates to prevent "Maximum update depth exceeded" React errors
                if (hasHands !== lastDetectionStateRef.current) {
                    lastDetectionStateRef.current = hasHands;
                    if (debounceTimeoutRef.current) {
                        clearTimeout(debounceTimeoutRef.current);
                    }
                    debounceTimeoutRef.current = setTimeout(() => {
                        setIsHandDetected(hasHands);
                    }, 50); // 50ms stable state required
                }

            } catch (err) {
                // Silently handle detection errors (frame timing issues)
            }

            animFrameRef.current = requestAnimationFrame(tick);
        };

        animFrameRef.current = requestAnimationFrame(tick);
    }, []);

    // Stop webcam + detection
    const stop = useCallback(() => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsRunning(false);
        setIsHandDetected(false);
        currentLandmarksRef.current = null;
    }, []);

    // Extract features from current landmarks
    const getFeatures = useCallback(() => {
        return extractFeatures(currentLandmarksRef.current);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => stop();
    }, [stop]);

    return useMemo(() => ({
        isModelLoaded,
        isLoading,
        isRunning,
        wasStarted,
        showVideo,
        setShowVideo,
        isHandDetected,
        get currentLandmarks() { return currentLandmarksRef.current; },
        start,
        stop,
        getFeatures,
        loadModel,
        error,
    }), [isModelLoaded, isLoading, isRunning, wasStarted, showVideo, isHandDetected, start, stop, getFeatures, loadModel, error]);
}
